import { useState } from 'react';

interface Props {
  brand?: string | null;
  /** Высота логотипа. Число → px. Строка → CSS-value (напр. '1.2em').
   *  По умолчанию '0.9em' — логотип подстраивается под шрифт родителя. */
  size?: number | string;
  /** Сколько множитель высоты разрешить по ширине. Default 4. */
  maxWidthRatio?: number;
}

/**
 * Нормализует название бренда в slug для поиска файла.
 * "ZIC" → "zic", "Liqui Moly" → "liqui-moly", "Mobil 1" → "mobil-1"
 */
export function brandToSlug(brand: string): string {
  return brand
    .toLowerCase()
    .trim()
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Отображает логотип бренда. Поведение:
 * - Файл лежит → показывается логотип. Название бренда присутствует в DOM
 *   невидимым слоем поверх, так что при выделении/копировании текста
 *   ("название товара + бренд") попадёт в буфер обмена как обычный текст.
 * - Файла нет (404) → onError прячет <img>, остаётся только текст бренда
 *   как fallback (дизайн не ломается).
 */
const BrandLogo: React.FC<Props> = ({ brand, size = '0.9em', maxWidthRatio = 4 }) => {
  const [hasLogo, setHasLogo] = useState(true);

  if (!brand) return null;

  const slug = brandToSlug(brand);
  const src = `/brands/${slug}/logo.webp`;
  const heightCss = typeof size === 'number' ? `${size}px` : size;

  if (!hasLogo) {
    // Fallback — обычный текст бренда
    return <span>{brand}</span>;
  }

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        verticalAlign: 'middle',
        lineHeight: 0,
      }}
    >
      <img
        src={src}
        alt={brand}
        style={{
          height: heightCss,
          width: 'auto',
          maxWidth: `calc(${heightCss} * ${maxWidthRatio})`,
          objectFit: 'contain',
          display: 'block',
        }}
        onError={() => setHasLogo(false)}
      />
      {/* Невидимый текст поверх картинки: для копирования через выделение.
          color: transparent — не виден глазами;
          user-select: text — разрешаем выделение;
          pointer-events: none — не блокирует клики по логотипу (напр. в OilOrdersPage
          по логотипу открывается drawer карточки товара). Выделение мышью
          всё равно работает через parent-элемент. */}
      <span
        aria-hidden="false"
        style={{
          position: 'absolute',
          inset: 0,
          color: 'transparent',
          fontSize: heightCss,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          userSelect: 'text',
          pointerEvents: 'none',
        }}
      >
        {brand}
      </span>
    </span>
  );
};

export default BrandLogo;
